import type { ReactNode } from "react";
import type { Course } from "@/types/course";
import { getTypeLabel } from "@/utils/courseType";
import { getSeedChipClass } from "@/utils/courseSeed";

interface ComparisonTableProps {
  courses: Course[];
  onRemove: (id: string) => void;
}

function PercentCell({ value }: { value: string }) {
  if (value === "-") {
    return (
      <span className="text-gray-400 dark:text-gray-500" title="데이터 없음">
        -
      </span>
    );
  }
  return <span className="text-gray-900 dark:text-gray-100">{value}</span>;
}

interface Row {
  label: string;
  render: (course: Course) => ReactNode;
}

export default function ComparisonTable({
  courses,
  onRemove,
}: ComparisonTableProps) {
  const rows: Row[] = [
    {
      label: "훈련기관",
      render: (c) => (
        <span className="text-gray-900 break-keep dark:text-gray-100">{c.institution}</span>
      ),
    },
    {
      label: "지역",
      render: (c) => <span className="text-gray-900 break-keep dark:text-gray-100">{c.region}</span>,
    },
    {
      label: "훈련유형",
      render: (c) => <span className="text-gray-900 break-keep dark:text-gray-100">{getTypeLabel(c.typeCode)}</span>,
    },
    {
      label: "정원",
      render: (c) => <span className="text-gray-900 dark:text-gray-100">{c.capacity}명</span>,
    },
    {
      label: "수강신청",
      render: (c) => <span className="text-gray-900 dark:text-gray-100">{c.applicants}명</span>,
    },
    {
      label: "정원충족률",
      render: (c) => <span className="text-gray-900 dark:text-gray-100">{c.fillRate}</span>,
    },
    {
      label: "수강비",
      render: (c) => (
        <span className="text-gray-900 dark:text-gray-100">{c.tuition.toLocaleString("ko-KR")}원</span>
      ),
    },
    {
      label: "취업률 3개월",
      render: (c) => <PercentCell value={c.employment3m} />,
    },
    {
      label: "취업률 6개월",
      render: (c) => <PercentCell value={c.employment6m} />,
    },
    {
      label: "만족도",
      render: (c) => <span className="text-gray-900 dark:text-gray-100">{c.satisfaction}점</span>,
    },
  ];

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 min-w-[120px] whitespace-nowrap border-b border-r border-gray-200 bg-white px-4 py-3 text-left text-xs font-medium text-gray-500 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400">
              비교 항목
            </th>
            {courses.map((course) => (
              <th
                key={course.id}
                className="min-w-[200px] border-b border-gray-200 px-4 py-3 text-left align-top dark:border-gray-800"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className={`mb-1.5 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${getSeedChipClass(course.seed)}`}>
                      {course.seed}
                    </span>
                    <div className="text-sm font-bold leading-snug text-gray-900 break-keep dark:text-gray-100">
                      {course.name}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemove(course.id)}
                    aria-label={`${course.name} 비교에서 제거`}
                    className="-m-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:focus-visible:ring-brand-400"
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300">
                      <i className="ri-close-line text-base" />
                    </span>
                  </button>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th className="sticky left-0 z-10 whitespace-nowrap border-b border-r border-gray-200 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-400">
                {row.label}
              </th>
              {courses.map((course) => (
                <td
                  key={course.id}
                  className="border-b border-gray-200 px-4 py-3 align-top text-sm break-keep dark:border-gray-800"
                >
                  {row.render(course)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}